import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Calendar, Save, Trash2 } from 'lucide-react';
import { useApp } from '@/store';
import { ConditionValue, Status } from '@/data/types';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { HoseCondition } from '@/data/types';

const conditionKeys: (keyof HoseCondition)[] = [
  'fireCock',
  'seals',
  'hose',
  'nozzle',
  'axle',
  'swivelArm',
  'doorLock',
  'water',
];

// Fallback defaults if store is empty — i18n-ignore
const DEFAULT_HOSE_DIAMETERS = ['16 mm', '19 mm', '25 mm'];
const DEFAULT_CONNECTION_DIAMETERS = ['24 mm', '27 mm', '34 mm'];

export function HoseEdit() {
  const t = useT();
  const { id, hoseId } = useParams();
  const nav = useNavigate();
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const workers = useApp(s => s.workers);
  const hoseConfig = useApp(s => s.hoseConfig);
  const toast = useApp(s => s.toast);
  const upsertHose = useApp(s => s.upsertHose);
  const deleteHose = useApp(s => s.deleteHose);
  const existing = customer?.hoses.find(h => h.id === hoseId);

  const [worker, setWorker] = useState(
    existing
      ? ((existing as any).worker ?? workers[0]?.name ?? '')
      : (workers[0]?.name ?? '')
  );
  const [floor, setFloor] = useState('');
  const [location, setLocation] = useState(existing?.location ?? '');
  const [mfr, setMfr] = useState(existing?.mfr ?? '');
  const [model, setModel] = useState(existing?.model ?? '');
  const [year, setYear] = useState(existing?.year?.toString() ?? '');
  const [length, setLength] = useState(existing?.length?.toString() ?? '');
  const [hoseDiameter, setHoseDiameter] = useState(
    existing?.diameter ?? '25 mm'
  );
  const [connectionDiameter, setConnectionDiameter] = useState(
    existing?.connectionDiameter ?? '34 mm'
  );
  const [connection, setConnection] = useState(
    existing?.connection ?? 'Storz C'
  );
  const [condition, setCondition] = useState<HoseCondition>(
    existing?.condition ?? {
      fireCock: ConditionValue.OK,
      seals: ConditionValue.OK,
      hose: ConditionValue.OK,
      nozzle: ConditionValue.OK,
      axle: ConditionValue.OK,
      swivelArm: ConditionValue.OK,
      doorLock: ConditionValue.OK,
      water: ConditionValue.OK,
    }
  );
  const [service, setService] = useState(existing?.lastService ?? '');
  const [next, setNext] = useState(existing?.nextService ?? '');
  const [resting, setResting] = useState(existing?.restingPressure ?? '');
  const [flowP, setFlowP] = useState(existing?.flowPressure ?? '');
  const [flow, setFlow] = useState(existing?.flowRate ?? '');
  const [test, setTest] = useState(existing?.pressureTestDate ?? '');

  const setToday = (setter: (v: string) => void) =>
    setter(new Date().toISOString().slice(0, 10));
  const computeNext = () => {
    if (service) {
      const d = new Date(service);
      d.setFullYear(d.getFullYear() + 1);
      setNext(d.toISOString().slice(0, 10));
    }
  };

  if (!customer) return <div>{t('common.notFound')}</div>;

  const handleSave = () => {
    upsertHose(customer.id, {
      id: existing?.id ?? `H-${Date.now()}`,
      location: location || undefined,
      mfr: mfr || undefined,
      model: model || undefined,
      year: year ? parseInt(year, 10) : undefined,
      length: length ? parseFloat(length) : undefined,
      diameter: hoseDiameter,
      connectionDiameter,
      connection: connection || undefined,
      condition,
      lastService: service || undefined,
      nextService: next || undefined,
      restingPressure: resting || undefined,
      flowPressure: flowP || undefined,
      flowRate: flow || undefined,
      pressureTestDate: test || undefined,
      spareParts: existing?.spareParts ?? [],
      status: existing?.status ?? Status.NONE,
    });
    toast(t('common.save'));
    nav(`/customer/${customer.id}/hoses`);
  };

  const handleDelete = () => {
    if (!existing) return;
    deleteHose(customer.id, existing.id);
    toast(t('common.delete'));
    nav(`/customer/${customer.id}/hoses`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div>
        <Link
          to={`/customer/${customer.id}/hoses`}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> {t('common.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight mt-2">
          {existing ? t('common.edit') : t('common.new')} {t('hose.fireHose')}
        </h1>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            <div>
              <Label>{t('hose.serviceTechnician')}</Label>
              <Select value={worker} onValueChange={setWorker}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {workers
                    .filter(w => w.active)
                    .map(w => (
                      <SelectItem key={w.id} value={w.name}>
                        {w.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('hose.equipment')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <Label>{t('ext.floor')}</Label>
              <Input value={floor} onChange={e => setFloor(e.target.value)} />
            </div>
            <div>
              <Label>{t('ext.location')}</Label>
              <Input
                data-testid="hose-location"
                value={location}
                onChange={e => setLocation(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('ext.manufacturer')}</Label>
              <Input
                data-testid="hose-mfr"
                value={mfr}
                onChange={e => setMfr(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('ext.model')}</Label>
              {hoseConfig.hoseModels.length > 0 ? (
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('ext.model')} />
                  </SelectTrigger>
                  <SelectContent>
                    {hoseConfig.hoseModels.map(m => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={model} onChange={e => setModel(e.target.value)} />
              )}
            </div>
            <div>
              <Label>{t('table.year')}</Label>
              <Input
                data-testid="hose-year"
                type="number"
                value={year}
                onChange={e => setYear(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('hose.length')}</Label>
              <Input
                data-testid="hose-length"
                type="number"
                value={length}
                onChange={e => setLength(e.target.value)}
              />
            </div>
            <div data-focus="hose-diameter">
              <Label>{t('hose.hoseDiameter')}</Label>
              <Select value={hoseDiameter} onValueChange={setHoseDiameter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(hoseConfig.hoseDiameters.length
                    ? hoseConfig.hoseDiameters
                    : DEFAULT_HOSE_DIAMETERS
                  ).map(d => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('hose.connectionDiameter')}</Label>
              <Select
                value={connectionDiameter}
                onValueChange={setConnectionDiameter}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(hoseConfig.connectionDiameters.length
                    ? hoseConfig.connectionDiameters
                    : DEFAULT_CONNECTION_DIAMETERS
                  ).map(d => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('hose.connection')}</Label>
              <Input
                value={connection}
                onChange={e => setConnection(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              {t('hose.conditionChecks')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {conditionKeys.map(k => (
              <div
                key={k}
                className="flex flex-col sm:grid sm:grid-cols-2 sm:items-center gap-1 sm:gap-2"
              >
                <Label className="text-xs capitalize">
                  {k.replace(/([A-Z])/g, ' $1').trim()}
                </Label>
                <Select
                  value={condition[k]}
                  onValueChange={v =>
                    setCondition({ ...condition, [k]: v as any })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ConditionValue.OK}>
                      {t('hose.ok')}
                    </SelectItem>
                    <SelectItem value={ConditionValue.NOT_OK}>
                      {t('hose.notOk')}
                    </SelectItem>
                    <SelectItem value={ConditionValue.NEW}>
                      {t('common.new')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('hose.serviceReport')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <Label>{t('hose.serviceDate')}</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="hose-service-date"
                  type="date"
                  value={service}
                  onChange={e => setService(e.target.value)}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setToday(setService)}
                >
                  <Calendar className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <Label>{t('hose.nextService')}</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="hose-next-service"
                  type="date"
                  value={next}
                  onChange={e => setNext(e.target.value)}
                />
                <Button variant="outline" size="sm" onClick={computeNext}>
                  +1y
                </Button>
              </div>
            </div>
            <div>
              <Label>{t('hose.restingPressure')}</Label>
              <Input
                data-testid="hose-resting-pressure"
                value={resting}
                onChange={e => setResting(e.target.value)}
                placeholder="e.g. 6.0 bar" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('hose.flowPressure')}</Label>
              <Input
                data-testid="hose-flow-pressure"
                value={flowP}
                onChange={e => setFlowP(e.target.value)}
                placeholder="e.g. 4.5 bar" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('hose.flowRate')}</Label>
              <Input
                data-testid="hose-flow-rate"
                value={flow}
                onChange={e => setFlow(e.target.value)}
                placeholder="e.g. 105 L/min" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('hose.pressureTestDate')}</Label>
              <Input
                data-testid="hose-pressure-test-date"
                type="date"
                value={test}
                onChange={e => setTest(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => nav(`/customer/${customer.id}/hoses`)}
        >
          {t('common.cancel')}
        </Button>
        {existing && (
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            {t('common.delete')}
          </Button>
        )}
        <Button data-testid="hose-save" onClick={handleSave}>
          <Save className="h-4 w-4" />
          {t('common.save')}
        </Button>
      </div>
    </motion.div>
  );
}
